import { Injectable, signal } from '@angular/core';
import { combineLatest, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { ApiService } from '../api/api.service';
import axios from 'axios';

import { AppStateService } from '../app-state/app-state.service';
import { PhotoResizeOutput, ResidentPhoto } from '@resparke/models/resident.model';
import { DeviceMode, VideoMessage } from '@resparke/models/client-types';


interface UploadProgress {
  progress: number;
  total: number;
  loaded: number;
  estimated: number;
}

export interface FileDeleteResponse {
  id: string;
  payload: string;
}


@Injectable({
  providedIn: 'root'
})
export class FileService {

  constructor(
    private apiService: ApiService,
    private appState: AppStateService,
  ) { }


  getSignedUrl(input: { key: string, action: 'GET' | 'PUT' }): Observable<string> {
    const statement = `
      query getSignedUrl($input: GetSignedUrlInput!) {
        getSignedUrl(input: $input)
      }
    `;
    return this.apiService
      .graphql<string>({ statement, variables: { input }, type: 'getSignedUrl' })
  }


  getFileBuffer(url: string): Observable<File> {
    return from(axios.get(url, {
      responseType: 'blob'
    })).pipe(
      map(response => response.data)
    );
  }

  uploadResidentPhoto({ key, file }: { key: string, file: File }): Observable<UploadProgress> {
    return this.getSignedUrl({ key, action: 'PUT' })
      .pipe(
        switchMap(url => this.upload(url, file)),
      );
  }

  uploadFile({ key, file }: { key: string, file: Blob }): Observable<UploadProgress> {
    return this.getSignedUrl({ key, action: 'PUT' })
      .pipe(
        switchMap(url => this.upload(url, file)),
      );
  }

  addVideoMessage(input: { id: string, residentId: string, callerId?: string, callerName?: string, filename: string, thumbnail?: string, duration?: number }): Observable<VideoMessage> {
    const statement = `
      mutation addVideoMessage($input: AddVideoMessageInput!) {
        addVideoMessage(input: $input) {
          action
          residentId
          id
          message {
            id
            residentId
            callerId
            callerName
            filename
            thumbnail
            duration
            dateCreated
          }
        }
      }
    `;
    return this.apiService
      .graphql<{ message: VideoMessage }>({ statement, variables: { input }, type: 'addVideoMessage' })
      .pipe(map(({ message }) => message))
  }

  generateVideoThumbnail(input: { key: string }): Observable<string | undefined> {
    const statement = `
      mutation generateVideoThumbnail($input: GenerateVideoThumbnailInput!) {
        generateVideoThumbnail(input: $input) {
          filename
        }
      }
    `;
    return this.apiService
      .graphql<{ filename?: string } | null>({ statement, variables: { input }, type: 'generateVideoThumbnail' })
      .pipe(map(result => result?.filename))
  }

  resizePhoto(input: { filename: string, extension: string, path: string }): Observable<PhotoResizeOutput> {

    const statement = `
      mutation resizePhoto($input: ResizePhotoInput!) {
        resizePhoto(input: $input) {
          size
          width
          height
          format
          hasThumbnail
        }
      }
    `;
    return this.apiService
      .graphql<PhotoResizeOutput>({ statement, variables: { input }, type: 'resizePhoto' })
  }

  getResidentPhotos(input: { residentId: string }): Observable<ResidentPhoto[]> {
    const statement = `
      query getResidentPhotos($residentId: ID!) {
        getResidentPhotos(residentId: $residentId) {
          id
          filename
          caption
          residentId
          type
          hasThumbnail
        }
      }
    `;
    return this.apiService
      .graphql<ResidentPhoto[]>({ statement, variables: input, type: 'getResidentPhotos' })
  }

  getPhotoObjects({ photos, facilityId, residentId }: { photos: ResidentPhoto[], facilityId: string, residentId: string }) {
    return of(photos).pipe(
      switchMap(photos => {
        if (!photos.length) {
          return of([])
        }
        const obs$: Observable<File>[] = [];
        for (const photo of photos) {
          let { filename } = photo;
          if (photo.hasThumbnail) {
            filename = filename.replace('.jpeg', '_thumbnail.jpeg');
          }
          const key = `facilities/${facilityId}/residents/${residentId}/${filename}`;
          obs$.push(this.getSignedUrl({ key, action: 'GET' })
            .pipe(
              switchMap(url => {
                return this.getFileBuffer(url);
              })
            ))
        }
        return combineLatest(obs$).pipe(
          // copy buffer over to photos
          map(files => {
            return files.map((file, index) => {
              const photo = photos.at(index);
              return {
                ...photo,
                file,
                imageUrl: signal<string | undefined>(undefined),
              }
            })
          })
        )
      })
    )
  }

  deletePhoto(input: { filename: string, id: string, mode: 'RESIDENT', modeId: string }): Observable<FileDeleteResponse> {
    const statement = `
      mutation deletePhoto($input: DeletePhotoInput!) {
        deletePhoto(input: $input) {
          id
          payload
        }
      }
    `;
    return this.apiService
      .graphql<FileDeleteResponse>({ statement, variables: { input }, type: 'deletePhoto' })
      .pipe(
        tap(() => {
          const residentPhotos = this.appState.get('residentPhotos').filter(photo => photo.id !== input.id);
          this.appState.set('residentPhotos', residentPhotos);
        })
      )
  }


  updatePhoto(input: { id: string, modeId: string, mode: 'RESIDENT', caption: string }): Observable<boolean> {
    const statement = `
      mutation updatePhoto($input: UpdatePhotoInput!) {
        updatePhoto(input: $input) {
          id
          payload
        }
      }
    `;
    return this.apiService
      .graphql<boolean>({ statement, variables: { input }, type: 'updatePhoto' })
      .pipe(
        tap(() => {
          const residentPhotos = this.appState.get('residentPhotos');
          const photo = residentPhotos.find(photo => photo.id === input.id);
          if (photo) {
            photo.caption = input.caption;
            this.appState.set('residentPhotos', residentPhotos);
          }
        })
      )
  }

  addPhoto(input: { id: string, type: string, filename: string, size: number, key: string, modeId: string, mode: DeviceMode, hasThumbnail: boolean }) {

    const statement = `
      mutation addPhoto($input: AddPhotoInput!) {
        addPhoto(input: $input) {
          id
          payload
        }
      }
    `;
    return this.apiService
      .graphql<any>({ statement, variables: { input }, type: 'addPhoto' })
  }

  private upload(url: string, file: Blob): Observable<UploadProgress> {
    return new Observable<UploadProgress>(subscriber => {
      axios.put(url, file, {
        headers: {
          'Content-Type': file.type
        }, onUploadProgress: (event => {
          subscriber.next({
            progress: event.progress,
            loaded: event.loaded,
            total: event.total,
            estimated: event.estimated
          })
        })
      }).then(() => {
        subscriber.complete();
      }).catch(error => {
        subscriber.error(error);
      });
    });
  }
}
